import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import {
  API_COMPANY_BULK_PAY_REIMBURSEMENT,
  API_COMPANY_CANCEL_REIMBURSEMENT,
  API_COMPANY_CREATE_EXPENSE,
  API_COMPANY_EXPENSE_ASSET_SYNC,
  API_COMPANY_EXPENSE_COMMENTS,
  API_COMPANY_EXPENSE_DECISION,
  API_COMPANY_DELETE_EXPENSE,
  API_COMPANY_SUBMIT_EXPENSE,
  API_COMPANY_UPDATE_EXPENSE,
  API_COMPANY_EXPENSE_PROJECTS,
  API_COMPANY_EXPENSE_PROJECT_BUDGET,
  API_COMPANY_EXPENSE_PROJECT_BUDGET_HISTORY,
  API_COMPANY_EXPENSE_PROJECT_BY_ID,
  API_COMPANY_EXPENSE_SETTINGS_CATEGORIES,
  API_COMPANY_EXPENSE_SETTINGS_CATEGORY,
  API_COMPANY_EXPENSE_SETTINGS_METHOD,
  API_COMPANY_EXPENSE_SETTINGS_METHODS,
  API_COMPANY_EXPENSE_SETTINGS_TYPE,
  API_COMPANY_EXPENSE_SETTINGS_TYPES,
  API_COMPANY_EXPENSE_STATS,
  API_COMPANY_GET_EXPENSES,
  API_COMPANY_GET_EXPENSE_BY_ID,
  API_COMPANY_GET_REIMBURSEMENTS,
  API_COMPANY_GET_REIMBURSEMENT_BY_ID,
  API_COMPANY_PAY_REIMBURSEMENT,
  API_COMPANY_REIMBURSEMENT_STATS,
} from "@/constants/apiEndpoints";
import type {
  IAddCommentPayload,
  IBulkPayReimbursementPayload,
  ICreateExpensePayload,
  IEditBudgetPayload,
  IExpense,
  IExpenseCategory,
  IExpenseComment,
  IExpenseDecisionPayload,
  IExpenseListItem,
  IExpenseStatsData,
  IExpenseTypeConfig,
  IListExpensesParams,
  IListReimbursementsParams,
  IPayReimbursementPayload,
  IProjectBudgetChange,
  IProjectBudgetDetail,
  IProjectBudgetHealth,
  ISyncToInventoryPayload,
  IReimbursement,
  IReimbursementDetail,
  IReimbursementMethodConfig,
  IReimbursementStatsData,
} from "@/types/(company)/expenses.interface";
import type { ENUM_EXPENSE_TYPE } from "@/utils/enums/expenses.enum";

export const companyExpensesApi = createApi({
  reducerPath: "companyExpensesApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "companyExpenses",
    "companyExpense",
    "companyExpenseComments",
    "companyReimbursements",
    "companyReimbursement",
    "companyExpenseProjects",
    "companyExpenseCategories",
    "companyExpenseTypes",
    "companyReimbursementMethods",
  ],
  endpoints: (builder) => ({
    // ── Expenses ──────────────────────────────────────────────────────────
    companyGetExpenses: builder.query<
      IApiPaginatedResponse<IExpenseListItem[]>,
      IListExpensesParams
    >({
      query: (params) => ({ url: API_COMPANY_GET_EXPENSES, params }),
      providesTags: ["companyExpenses"],
    }),
    companyGetExpenseStats: builder.query<
      IApiResponse<IExpenseStatsData>,
      { type?: ENUM_EXPENSE_TYPE } | void
    >({
      query: (params) => ({
        url: API_COMPANY_EXPENSE_STATS,
        params: params ?? {},
      }),
      providesTags: ["companyExpenses"],
    }),
    companyGetExpenseById: builder.query<IApiResponse<IExpense>, number>({
      query: (id) => ({ url: API_COMPANY_GET_EXPENSE_BY_ID(id) }),
      providesTags: ["companyExpense"],
    }),
    companyCreateExpense: builder.mutation<
      IApiResponse<IExpense>,
      ICreateExpensePayload
    >({
      query: (body) => ({ url: API_COMPANY_CREATE_EXPENSE, method: "POST", body }),
      invalidatesTags: ["companyExpenses"],
    }),
    // Correct an undecided expense (draft / submitted / changes requested).
    // Projects too: a submitted edit changes pending counts and cap usage.
    companyUpdateExpense: builder.mutation<
      IApiResponse<IExpense>,
      { id: number; payload: Partial<ICreateExpensePayload> }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_UPDATE_EXPENSE(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "companyExpenses",
        "companyExpense",
        "companyExpenseProjects",
      ],
    }),
    // Soft-delete an undecided expense (draft / submitted / changes requested).
    companyDeleteExpense: builder.mutation<
      IApiResponse<{ deleted: boolean }>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_DELETE_EXPENSE(id), method: "DELETE" }),
      invalidatesTags: [
        "companyExpenses",
        "companyExpense",
        "companyExpenseProjects",
      ],
    }),
    // Submit a draft or revision for approval on the employee's behalf.
    companySubmitExpense: builder.mutation<IApiResponse<IExpense>, number>({
      query: (id) => ({ url: API_COMPANY_SUBMIT_EXPENSE(id), method: "POST" }),
      invalidatesTags: [
        "companyExpenses",
        "companyExpense",
        "companyExpenseProjects",
      ],
    }),
    companyDecideExpense: builder.mutation<
      IApiResponse<IExpense>,
      { id: number; payload: IExpenseDecisionPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_DECISION(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyExpenses", "companyExpense", "companyReimbursements"],
    }),
    companyAssetSyncExpense: builder.mutation<
      IApiResponse<unknown>,
      { id: number; payload?: ISyncToInventoryPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_ASSET_SYNC(id),
        method: "POST",
        body: payload ?? {},
      }),
      invalidatesTags: ["companyExpense", "companyExpenses"],
    }),

    // ── Comments ──────────────────────────────────────────────────────────
    companyGetExpenseComments: builder.query<
      IApiResponse<IExpenseComment[]>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_EXPENSE_COMMENTS(id) }),
      providesTags: ["companyExpenseComments"],
    }),
    companyAddExpenseComment: builder.mutation<
      IApiResponse<IExpenseComment>,
      { id: number; payload: IAddCommentPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_COMMENTS(id),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["companyExpenseComments", "companyExpense"],
    }),

    // ── Reimbursements ──────────────────────────────────────────────────────
    companyGetReimbursements: builder.query<
      IApiPaginatedResponse<IReimbursement[]>,
      IListReimbursementsParams
    >({
      query: (params) => ({ url: API_COMPANY_GET_REIMBURSEMENTS, params }),
      providesTags: ["companyReimbursements"],
    }),
    companyGetReimbursementStats: builder.query<
      IApiResponse<IReimbursementStatsData>,
      void
    >({
      query: () => ({ url: API_COMPANY_REIMBURSEMENT_STATS }),
      providesTags: ["companyReimbursements"],
    }),
    companyGetReimbursementById: builder.query<
      IApiResponse<IReimbursementDetail>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_GET_REIMBURSEMENT_BY_ID(id) }),
      providesTags: ["companyReimbursement"],
    }),
    companyPayReimbursement: builder.mutation<
      IApiResponse<IReimbursement>,
      { id: number; payload: IPayReimbursementPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_PAY_REIMBURSEMENT(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyReimbursements", "companyReimbursement"],
    }),
    companyBulkPayReimbursement: builder.mutation<
      IApiResponse<{ paid: number[]; count: number }>,
      IBulkPayReimbursementPayload
    >({
      query: (body) => ({
        url: API_COMPANY_BULK_PAY_REIMBURSEMENT,
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyReimbursements"],
    }),
    companyCancelReimbursement: builder.mutation<
      IApiResponse<IReimbursement>,
      { id: number; reason: string }
    >({
      query: ({ id, reason }) => ({
        url: API_COMPANY_CANCEL_REIMBURSEMENT(id),
        method: "PUT",
        body: { reason },
      }),
      invalidatesTags: ["companyReimbursements", "companyReimbursement"],
    }),

    // ── Project budgets ─────────────────────────────────────────────────────
    companyGetExpenseProjects: builder.query<
      IApiResponse<IProjectBudgetHealth[]>,
      { health?: "active" | "warning" | "over" } | void
    >({
      query: (params) => ({ url: API_COMPANY_EXPENSE_PROJECTS, params: params ?? {} }),
      providesTags: ["companyExpenseProjects"],
    }),
    companyGetExpenseProjectById: builder.query<
      IApiResponse<IProjectBudgetDetail>,
      number
    >({
      query: (id) => ({ url: API_COMPANY_EXPENSE_PROJECT_BY_ID(id) }),
      providesTags: ["companyExpenseProjects"],
    }),
    companyEditProjectBudget: builder.mutation<
      IApiResponse<{ budget: IProjectBudgetDetail; warning: string | null }>,
      { id: number; payload: IEditBudgetPayload }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_PROJECT_BUDGET(id),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["companyExpenseProjects"],
    }),
    // Same tag as the project reads, so a budget edit refreshes the history.
    companyGetProjectBudgetHistory: builder.query<
      IApiPaginatedResponse<IProjectBudgetChange[]>,
      { id: number; page: number; limit?: number }
    >({
      query: ({ id, page, limit = 10 }) => ({
        url: API_COMPANY_EXPENSE_PROJECT_BUDGET_HISTORY(id),
        params: { page, limit },
      }),
      providesTags: ["companyExpenseProjects"],
    }),

    // ── Settings ──────────────────────────────────────────────────────────
    companyGetExpenseCategories: builder.query<
      IApiResponse<IExpenseCategory[]>,
      void
    >({
      query: () => ({ url: API_COMPANY_EXPENSE_SETTINGS_CATEGORIES }),
      providesTags: ["companyExpenseCategories"],
    }),
    companyCreateExpenseCategory: builder.mutation<
      IApiResponse<IExpenseCategory>,
      Partial<IExpenseCategory>
    >({
      query: (body) => ({
        url: API_COMPANY_EXPENSE_SETTINGS_CATEGORIES,
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyExpenseCategories"],
    }),
    companyUpdateExpenseCategory: builder.mutation<
      IApiResponse<IExpenseCategory>,
      { id: number; payload: Partial<IExpenseCategory> }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_SETTINGS_CATEGORY(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyExpenseCategories"],
    }),
    companyGetExpenseTypes: builder.query<IApiResponse<IExpenseTypeConfig[]>, void>({
      query: () => ({ url: API_COMPANY_EXPENSE_SETTINGS_TYPES }),
      providesTags: ["companyExpenseTypes"],
    }),
    companyCreateExpenseType: builder.mutation<
      IApiResponse<IExpenseTypeConfig>,
      Partial<IExpenseTypeConfig>
    >({
      query: (body) => ({
        url: API_COMPANY_EXPENSE_SETTINGS_TYPES,
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyExpenseTypes"],
    }),
    companyUpdateExpenseType: builder.mutation<
      IApiResponse<IExpenseTypeConfig>,
      { id: number; payload: Partial<IExpenseTypeConfig> }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_SETTINGS_TYPE(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyExpenseTypes"],
    }),
    companyGetReimbursementMethods: builder.query<
      IApiResponse<IReimbursementMethodConfig[]>,
      void
    >({
      query: () => ({ url: API_COMPANY_EXPENSE_SETTINGS_METHODS }),
      providesTags: ["companyReimbursementMethods"],
    }),
    companyUpdateReimbursementMethod: builder.mutation<
      IApiResponse<IReimbursementMethodConfig>,
      { id: number; payload: { label?: string; enabled?: boolean } }
    >({
      query: ({ id, payload }) => ({
        url: API_COMPANY_EXPENSE_SETTINGS_METHOD(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["companyReimbursementMethods"],
    }),
  }),
});

export const {
  useCompanyGetExpensesQuery,
  useCompanyGetExpenseStatsQuery,
  useCompanyGetExpenseByIdQuery,
  useCompanyCreateExpenseMutation,
  useCompanyDecideExpenseMutation,
  useCompanyAssetSyncExpenseMutation,
  useCompanyGetExpenseCommentsQuery,
  useCompanyAddExpenseCommentMutation,
  useCompanyGetReimbursementsQuery,
  useCompanyGetReimbursementStatsQuery,
  useCompanyGetReimbursementByIdQuery,
  useCompanyPayReimbursementMutation,
  useCompanyBulkPayReimbursementMutation,
  useCompanyCancelReimbursementMutation,
  useCompanyGetExpenseProjectsQuery,
  useCompanyGetExpenseProjectByIdQuery,
  useCompanyUpdateExpenseMutation,
  useCompanySubmitExpenseMutation,
  useCompanyDeleteExpenseMutation,
  useCompanyGetProjectBudgetHistoryQuery,
  useCompanyEditProjectBudgetMutation,
  useCompanyGetExpenseCategoriesQuery,
  useCompanyCreateExpenseCategoryMutation,
  useCompanyUpdateExpenseCategoryMutation,
  useCompanyGetExpenseTypesQuery,
  useCompanyCreateExpenseTypeMutation,
  useCompanyUpdateExpenseTypeMutation,
  useCompanyGetReimbursementMethodsQuery,
  useCompanyUpdateReimbursementMethodMutation,
} = companyExpensesApi;
